// ============================================================================
// SHORTAGE BOARD HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { shortageService } from 'api/services/shortageService';
import type { ShortageBoardQuery, ShortageNight } from 'api/types/shortage.types';

/**
 * Reads the shortage board.
 *
 * There is no client-side caching of the result and there must not be: the
 * shortage is derived from live inventory, so a cancellation elsewhere in the
 * property makes a row here wrong immediately. A board showing a shortage that no
 * longer exists is exactly how operational boards come to be ignored.
 */
export const useShortageBoard = (query: ShortageBoardQuery | null) => {
  const [data, setData] = useState<ShortageNight[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const queryKey = JSON.stringify(query);

  const fetchBoard = useCallback(async () => {
    if (!query?.propertyId) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await shortageService.getBoard(query));
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  return { data, loading, error, refetch: fetchBoard };
};

export default useShortageBoard;
